/**
 * State of the first-run setup wizard, shared with the step components
 * through a context.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFieldArray, useForm, type Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  CURRENCIES,
  INDUSTRIES,
  QR_REFERENCE_TYPES,
  SUPPORTED_LOCALES,
  isQrIban,
  isValidIban,
  normalizeIban,
  type Locale,
} from "@offertli/shared";
import { useSetupStatus } from "../../components/guards.js";
import { Card } from "@primereact/ui/card";
import { apiGet, apiPost, isApiError } from "../../lib/api.js";
import { useInvalidateSession, type SessionUser } from "../../lib/auth.js";
import { APP_CONFIG_QUERY_KEY } from "../../lib/app-config.js";
import { setLocale } from "../../i18n/index.js";
import { createContext, useContext } from "react";

export const PASSWORD_MIN = 10;
export const RATE_RE = /^\d{1,3}([.,]\d{1,4})?$/;
export const TIMEZONES = [
  "Europe/Zurich",
  "Europe/Berlin",
  "Europe/Vienna",
  "Europe/Paris",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Rome",
  "UTC",
] as const;
export const COUNTRIES = ["CH", "LI", "DE", "AT", "FR", "IT"] as const;
export type Country = (typeof COUNTRIES)[number];
export type Industry = (typeof INDUSTRIES)[number];
export const INDUSTRY_DEFAULT_LABELS: Record<Industry, string> = {
  landscaping: "Landscaping",
  general_services: "General services",
  construction: "Construction",
  cleaning: "Cleaning",
  other: "Other",
};
export interface BrowserDefaults {
  locale: Locale;
  country: Country;
}
export function getBrowserDefaults(): BrowserDefaults {
  const languageTags =
    typeof navigator === "undefined"
      ? []
      : navigator.languages.length
        ? navigator.languages
        : [navigator.language];
  let locale: Locale = "en";
  let country: Country = "CH";
  for (const languageTag of languageTags) {
    try {
      const language = new Intl.Locale(languageTag).language.toLowerCase();
      if ((SUPPORTED_LOCALES as readonly string[]).includes(language)) {
        locale = language as Locale;
        break;
      }
    } catch {
      // Ignore malformed browser language tags.
    }
  }
  for (const languageTag of languageTags) {
    try {
      const region = new Intl.Locale(languageTag).maximize().region;
      if (
        region &&
        (COUNTRIES as readonly string[]).includes(region.toUpperCase())
      ) {
        country = region.toUpperCase() as Country;
        break;
      }
    } catch {
      // Ignore malformed browser language tags.
    }
  }
  return {
    locale,
    country,
  };
}
export const optionalText = z.string().trim();
export const requiredText = z.string().trim().min(1, "validation.required");
export const taxRateSchema = z.object({
  name: requiredText,
  rate: z
    .string()
    .trim()
    .min(1, "validation.required")
    .regex(RATE_RE, "validation.rateRange")
    .refine((value) => {
      const parsed = Number(value.replace(",", "."));
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
    }, "validation.rateRange"),
  isDefault: z.boolean(),
});
export const setupSchema = z
  .object({
    uiLocale: z.enum(SUPPORTED_LOCALES),
    company: z.object({
      industry: z.enum(INDUSTRIES),
      name: requiredText,
      tradingName: optionalText,
      vatNumber: optionalText,
      website: optionalText,
      addressLine1: requiredText,
      addressLine2: optionalText,
      postalCode: requiredText,
      city: requiredText,
      canton: optionalText,
      country: requiredText,
      email: requiredText.email("validation.email"),
      phone: optionalText,
    }),
    systemAdmin: z
      .object({
        password: z.string().min(PASSWORD_MIN, "validation.passwordMin"),
        confirmPassword: z.string().min(1, "validation.required"),
      })
      .refine((values) => values.password === values.confirmPassword, {
        path: ["confirmPassword"],
        message: "validation.passwordMismatch",
      }),
    localization: z.object({
      defaultLocale: z.enum(SUPPORTED_LOCALES),
      timezone: requiredText,
      currency: z.enum(CURRENCIES),
    }),
    vatRegistered: z.boolean(),
    taxRates: z.array(taxRateSchema).min(1, "validation.minOneRate"),
    referenceType: z.enum(QR_REFERENCE_TYPES),
    iban: optionalText,
    bankName: optionalText,
    paymentTermDays: z.coerce
      .number({
        invalid_type_error: "validation.invalidNumber",
      })
      .int("validation.invalidNumber")
      .min(0, "validation.invalidNumber")
      .max(365, "validation.invalidNumber"),
    offerValidityDays: z.coerce
      .number({
        invalid_type_error: "validation.invalidNumber",
      })
      .int("validation.invalidNumber")
      .min(1, "validation.invalidNumber")
      .max(365, "validation.invalidNumber"),
  })
  .superRefine((values, ctx) => {
    const iban = values.iban;
    if (iban.length > 0 && !isValidIban(iban)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["iban"],
        message: "validation.iban",
      });
      return;
    }
    if (values.referenceType === "QRR") {
      if (iban.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["iban"],
          message: "validation.required",
        });
      } else if (!isQrIban(iban)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["iban"],
          message: "validation.qrIbanRequired",
        });
      }
    }
  });
export type SetupForm = z.infer<typeof setupSchema>;
export interface Credential {
  kind: "setup" | "invite";
  token: string;
  email?: string;
}
export const STEP_FIELDS: Array<Array<Path<SetupForm>>> = [
  [],
  // credential — validated against the API instead
  ["uiLocale"],
  ["company.industry"],
  [
    "company.name",
    "company.tradingName",
    "company.vatNumber",
    "company.website",
  ],
  [
    "company.addressLine1",
    "company.addressLine2",
    "company.postalCode",
    "company.city",
    "company.canton",
    "company.country",
    "company.email",
    "company.phone",
  ],
  ["systemAdmin.password", "systemAdmin.confirmPassword"],
  [
    "localization.defaultLocale",
    "localization.timezone",
    "localization.currency",
  ],
  ["taxRates"],
  ["iban", "bankName", "referenceType"],
  ["paymentTermDays", "offerValidityDays"],
  [], // review
];
export const STEP_KEYS = [
  "credential",
  "language",
  "industry",
  "company",
  "address",
  "systemAdmin",
  "localization",
  "vat",
  "bank",
  "numbering",
  "review",
] as const;
export function omitEmpty(value: string): string | undefined {
  return value.length > 0 ? value : undefined;
}

export function useSetupWizard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setupStatus = useSetupStatus();
  const invalidateSession = useInvalidateSession();
  const queryClient = useQueryClient();
  const browserDefaults = useMemo(getBrowserDefaults, []);
  const uiLocaleChanged = useRef(false);
  const documentLocaleChanged = useRef(false);
  const [step, setStep] = useState(0);
  const [credential, setCredential] = useState<Credential | null>(null);
  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    control,
    trigger,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SetupForm>({
    resolver: zodResolver(setupSchema),
    mode: "onTouched",
    defaultValues: {
      uiLocale: browserDefaults.locale,
      company: {
        industry: "landscaping",
        name: "",
        tradingName: "",
        vatNumber: "",
        website: "",
        addressLine1: "",
        addressLine2: "",
        postalCode: "",
        city: "",
        canton: "",
        country: browserDefaults.country,
        email: "",
        phone: "",
      },
      systemAdmin: {
        password: "",
        confirmPassword: "",
      },
      localization: {
        defaultLocale: browserDefaults.locale,
        timezone: "Europe/Zurich",
        currency: "CHF",
      },
      vatRegistered: true,
      taxRates: [
        {
          name: t("setup.vat.standardRate"),
          rate: "8.1",
          isDefault: true,
        },
      ],
      referenceType: "NON",
      iban: "",
      bankName: "",
      paymentTermDays: 30,
      offerValidityDays: 30,
    },
  });
  const rates = useFieldArray({
    control,
    name: "taxRates",
  });
  const values = watch();
  const suggestedLocale =
    setupStatus.data?.defaultLocale &&
    (SUPPORTED_LOCALES as readonly string[]).includes(
      setupStatus.data.defaultLocale,
    )
      ? (setupStatus.data.defaultLocale as Locale)
      : null;
  const preferredLocale = suggestedLocale ?? browserDefaults.locale;
  const localeChoices = useMemo(
    () => [
      preferredLocale,
      ...SUPPORTED_LOCALES.filter((locale) => locale !== preferredLocale),
    ],
    [preferredLocale],
  );
  const countryChoices = useMemo(
    () => [
      browserDefaults.country,
      ...COUNTRIES.filter((country) => country !== browserDefaults.country),
    ],
    [browserDefaults.country],
  );

  // OFFERTLI_DEFAULT_LOCALE takes precedence over the browser suggestion.
  useEffect(() => {
    if (!suggestedLocale) return;
    if (!uiLocaleChanged.current) {
      setValue("uiLocale", suggestedLocale);
      setLocale(suggestedLocale);
    }
    if (!uiLocaleChanged.current && !documentLocaleChanged.current) {
      setValue("localization.defaultLocale", suggestedLocale);
    }
  }, [suggestedLocale, setValue]);

  // Invitation links prove setup access; they do not create a user identity.
  const inviteToken = searchParams.get("invite");
  useEffect(() => {
    if (!inviteToken || credential) return;
    setValidating(true);
    setCredentialError(null);
    apiGet<{
      valid: true;
      email: string;
    }>(`/setup/invitation/${encodeURIComponent(inviteToken)}`)
      .then((res) => {
        setCredential({
          kind: "invite",
          token: inviteToken,
          email: res.email,
        });
      })
      .catch(() => setCredentialError(t("setup.credential.inviteInvalid")))
      .finally(() => setValidating(false));
    // Runs once per invite token; credential guard prevents re-entry.
  }, [inviteToken, credential, setValue, t]);
  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], {
        type: "region",
      });
    } catch {
      return null;
    }
  }, [i18n.language]);
  const validateSetupToken = async () => {
    const token = tokenInput.trim();
    if (!token) {
      setCredentialError(t("validation.tokenRequired"));
      return;
    }
    setValidating(true);
    setCredentialError(null);
    try {
      await apiPost<{
        valid: true;
      }>("/setup/validate-token", {
        token,
      });
      setCredential({
        kind: "setup",
        token,
      });
    } catch {
      setCredentialError(t("setup.credential.invalid"));
    } finally {
      setValidating(false);
    }
  };
  const goNext = async () => {
    if (step === 0) {
      if (credential) setStep(1);
      return;
    }
    const fields = STEP_FIELDS[step] ?? [];
    const valid = fields.length === 0 || (await trigger(fields));
    if (valid)
      setStep((current) => Math.min(current + 1, STEP_KEYS.length - 1));
  };
  const onSubmit = handleSubmit(async (form) => {
    if (!credential) return;
    setSubmitError(null);
    const payload = {
      credential:
        credential.kind === "setup"
          ? {
              setupToken: credential.token,
            }
          : {
              invitationToken: credential.token,
            },
      locale: form.uiLocale,
      company: {
        industry: form.company.industry,
        name: form.company.name,
        tradingName: omitEmpty(form.company.tradingName),
        addressLine1: form.company.addressLine1,
        addressLine2: omitEmpty(form.company.addressLine2),
        postalCode: form.company.postalCode,
        city: form.company.city,
        canton: omitEmpty(form.company.canton),
        country: form.company.country,
        email: form.company.email,
        phone: omitEmpty(form.company.phone),
        website: omitEmpty(form.company.website),
        vatNumber: omitEmpty(form.company.vatNumber),
      },
      systemAdmin: {
        password: form.systemAdmin.password,
      },
      localization: form.localization,
      billing: {
        vatRegistered: form.vatRegistered,
        paymentTermDays: form.paymentTermDays,
        iban: form.iban ? normalizeIban(form.iban) : undefined,
        bankName: omitEmpty(form.bankName),
        referenceType: form.referenceType,
        taxRates: form.taxRates.map((rate) => ({
          name: rate.name,
          rate: rate.rate.replace(",", "."),
          isDefault: rate.isDefault,
        })),
      },
      documents: {
        offerValidityDays: form.offerValidityDays,
      },
    };
    try {
      await apiPost<{
        user: SessionUser;
      }>("/setup/complete", payload);
      await invalidateSession();
      await queryClient.invalidateQueries({
        queryKey: APP_CONFIG_QUERY_KEY,
      });
      navigate("/", {
        replace: true,
      });
    } catch (error) {
      setSubmitError(
        isApiError(error) ? error.message : t("setup.review.failed"),
      );
    }
  });
  const countryName = (code: string): string => regionNames?.of(code) ?? code;
  const year = new Date().getFullYear();
  const stepKey = STEP_KEYS[step] ?? "credential";
  const stepTitle =
    stepKey === "industry"
      ? t("setup.steps.industry", {
          defaultValue: "Industry",
        })
      : t(`setup.steps.${stepKey}`);
  return {
    t,
    i18n,
    register,
    control,
    trigger,
    watch,
    setValue,
    errors,
    isSubmitting,
    rates,
    values,
    setupStatus,
    suggestedLocale,
    preferredLocale,
    localeChoices,
    countryChoices,
    uiLocaleChanged,
    documentLocaleChanged,
    credential,
    setCredential,
    credentialError,
    setCredentialError,
    validating,
    setValidating,
    tokenInput,
    setTokenInput,
    submitError,
    validateSetupToken,
    goNext,
    onSubmit,
    countryName,
    year,
    regionNames,
    browserDefaults,
    inviteToken,
    step,
    setStep,
    stepKey,
    stepTitle,
  };
}

export type SetupWizard = ReturnType<typeof useSetupWizard>;
export const SetupContext = createContext<SetupWizard | null>(null);

export function useSetup(): SetupWizard {
  const wizard = useContext(SetupContext);
  if (!wizard) throw new Error("Setup steps must render inside the wizard");
  return wizard;
}

export function ReviewSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card.Root padded={false} className="overflow-hidden">
      <Card.Body>
        <Card.Content>
          <h3 className="border-b border-app-border px-4 py-2.5 text-sm font-medium text-app-text">
            {title}
          </h3>
          <dl className="divide-y divide-app-border">{children}</dl>
        </Card.Content>
      </Card.Body>
    </Card.Root>
  );
}
export function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <dt className="shrink-0 text-sm text-app-muted">{label}</dt>
      <dd className="text-right text-sm text-app-text">{value}</dd>
    </div>
  );
}
